/**
 * TeacherDashboard - 学習分析ダッシュボード
 * 教師向け生徒分析画面
 * - 上段: クラス概要パネル（D3可視化）
 * - 下段: 個別生徒一覧 + 詳細5パネル
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { teacherAPI, authAPI, analyticsAPI } from '../utils/api';
import * as d3 from 'd3';
import { logClientError } from '../utils/safeLog';
import { buildClassSummaryRows, downloadCsv, rowsToCsv } from '../utils/teacherExport';
import { COGNITIVE_KEYS, triggerReasonLabel } from '../utils/triggerReasonLabel';

// ==================== Label Key Sets ====================
// Labels are resolved at render time via t():
//  - graph types     -> t('graphType.<type>')      (shared table)
//  - cognitive axes   -> t('dashboard.cog.<key>')
//  - source labels    -> t('dashboard.source.<key>')
//  - learning style    -> t('dashboard.learningStyle.<axis>')
//  - support levels    -> t('dashboard.supportLevel.<Lv>')
//  - trigger reasons   -> t('dashboard.trigger.<code>')

// Cognitive axes and trigger codes, and how a trigger_reason is labelled: utils/triggerReasonLabel.js
// (an unknown code is labelled "other", never shown or exported as raw text).

// ==================== Panel help ====================
// "How to read this": what each figure is actually computed from, so a count of
// operations is not read as a measure of weakness, and a self-report is not read
// as an assessment. Collapsed by default to keep the panels compact.
function PanelHelp({ text, toggleLabel }) {
  return (
    <details className="teacher-panel-help" style={{ marginBottom: '10px', fontSize: '0.85em', color: 'var(--text-muted)' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{toggleLabel}</summary>
      <p style={{ margin: '6px 0 0', lineHeight: 1.6 }}>{text}</p>
    </details>
  );
}

// ==================== D3 Chart Colors ====================
const CHART_COLORS = [
  '#5a7d8c', '#c87941', '#6b8a6b', '#8c6b8a',
  '#7d8c5a', '#8c5a5a', '#5a8c8c', '#8c7d5a',
];

const SEVERITY_COLORS = {
  0: '#bdbdbd', // gray
  1: '#fdd835', // yellow
  2: '#fb8c00', // orange
  3: '#e53935', // red
};

// ==================== D3 Chart Helpers ====================

/** Shared tooltip helper */
function getTooltip() {
  return d3.select('body').selectAll('.d3-tooltip').data([0]).join('div')
    .attr('class', 'd3-tooltip')
    .style('position', 'absolute')
    .style('background', 'rgba(0,0,0,0.8)')
    .style('color', '#fff')
    .style('padding', '6px 10px')
    .style('border-radius', '4px')
    .style('font-size', '13px')
    .style('pointer-events', 'none')
    .style('opacity', 0)
    .style('z-index', 9999);
}

// Tier-2 a11y: a teacher chart is otherwise silent to screen readers. Expose it
// as one image whose name lists every item and its count (the panel heading
// above already names the chart).
function setChartAccessibleName(svg, data) {
  const summary = (data || []).map((d) => `${d.label} ${d.value}`).join(', ');
  svg.attr('role', 'img').attr('aria-label', summary);
}

/**
 * D3 横棒グラフ描画
 */
function drawHorizontalBarChart(svgEl, data, {
  width = 400, height = null,
  margin = { top: 20, right: 40, bottom: 30, left: 130 },
  colorFn = null,
} = {}) {
  if (!svgEl || !data || data.length === 0) return;

  const barHeight = 28;
  const computedHeight = height || (margin.top + margin.bottom + data.length * (barHeight + 8));

  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();
  svg.attr('width', width).attr('height', computedHeight);
  setChartAccessibleName(svg, data);

  const innerW = width - margin.left - margin.right;
  const innerH = computedHeight - margin.top - margin.bottom;

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

  const y = d3.scaleBand().domain(data.map(d => d.label)).range([0, innerH]).padding(0.25);
  const x = d3.scaleLinear().domain([0, d3.max(data, d => d.value) || 1]).nice().range([0, innerW]);

  g.append('g')
    .call(d3.axisLeft(y).tickSize(0))
    .selectAll('text')
    .style('font-size', '13px')
    .style('fill', 'var(--text, #333)');
  g.select('.domain').remove();

  const bars = g.selectAll('.bar')
    .data(data).enter().append('rect')
    .attr('class', 'bar')
    .attr('y', d => y(d.label))
    .attr('height', y.bandwidth())
    .attr('x', 0)
    .attr('width', d => x(d.value))
    .attr('rx', 3)
    .attr('fill', (d, i) => colorFn ? colorFn(d, i) : CHART_COLORS[i % CHART_COLORS.length])
    .style('cursor', 'pointer');

  g.selectAll('.bar-value')
    .data(data).enter().append('text')
    .attr('x', d => x(d.value) + 6)
    .attr('y', d => y(d.label) + y.bandwidth() / 2)
    .attr('dy', '0.35em')
    .style('font-size', '13px')
    .style('font-weight', '600')
    .style('fill', 'var(--text, #333)')
    .text(d => d.value);

  const tooltip = getTooltip();
  bars
    .on('mouseover', function(event, d) {
      d3.select(this).attr('opacity', 0.8);
      tooltip.style('opacity', 1).html(`<strong>${d.label}</strong>: ${d.value}`);
    })
    .on('mousemove', function(event) {
      tooltip.style('left', (event.pageX + 12) + 'px').style('top', (event.pageY - 28) + 'px');
    })
    .on('mouseout', function() {
      d3.select(this).attr('opacity', 1);
      tooltip.style('opacity', 0);
    });
}

/**
 * D3 折れ線グラフ描画 (学習進捗)
 */
function drawLineChart(svgEl, data, {
  width = 400, height = 220,
  margin = { top: 20, right: 30, bottom: 40, left: 50 },
  t = (k) => k,
} = {}) {
  if (!svgEl || !data || data.length === 0) return;

  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();
  svg.attr('width', width).attr('height', height);
  setChartAccessibleName(svg, data);

  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

  const x = d3.scalePoint().domain(data.map(d => d.label)).range([0, innerW]).padding(0.5);
  const y = d3.scaleLinear().domain([0, d3.max(data, d => d.value) || 1]).nice().range([innerH, 0]);

  // Grid
  g.append('g').attr('class', 'grid')
    .call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(''))
    .selectAll('line').style('stroke', 'var(--border, #ddd)').style('stroke-opacity', 0.5);
  g.select('.grid .domain').remove();

  // X axis
  g.append('g').attr('transform', `translate(0,${innerH})`)
    .call(d3.axisBottom(x).tickSize(0))
    .selectAll('text')
    .style('font-size', '12px').style('fill', 'var(--text-muted, #777)')
    .attr('transform', 'rotate(-30)').attr('text-anchor', 'end');

  // Y axis
  g.append('g').call(d3.axisLeft(y).ticks(5))
    .selectAll('text').style('font-size', '12px').style('fill', 'var(--text-muted, #777)');

  // Line + area
  const line = d3.line().x(d => x(d.label)).y(d => y(d.value)).curve(d3.curveMonotoneX);
  const area = d3.area().x(d => x(d.label)).y0(innerH).y1(d => y(d.value)).curve(d3.curveMonotoneX);

  g.append('path').datum(data).attr('fill', CHART_COLORS[0]).attr('fill-opacity', 0.08).attr('d', area);
  g.append('path').datum(data).attr('fill', 'none').attr('stroke', CHART_COLORS[0]).attr('stroke-width', 2.5).attr('d', line);

  // Dots
  const tooltip = getTooltip();
  g.selectAll('.dot').data(data).enter().append('circle')
    .attr('cx', d => x(d.label)).attr('cy', d => y(d.value))
    .attr('r', 4).attr('fill', '#fff').attr('stroke', CHART_COLORS[0]).attr('stroke-width', 2)
    .style('cursor', 'pointer')
    .on('mouseover', function(event, d) {
      d3.select(this).attr('r', 6);
      tooltip.style('opacity', 1).html(`<strong>${d.label}</strong>: ${t('dashboard.teacher.timesCount', { count: d.value })}`);
    })
    .on('mousemove', function(event) {
      tooltip.style('left', (event.pageX + 12) + 'px').style('top', (event.pageY - 28) + 'px');
    })
    .on('mouseout', function() {
      d3.select(this).attr('r', 4);
      tooltip.style('opacity', 0);
    });
}

/**
 * D3 操作源棒グラフ
 */
function drawSourceBarChart(svgEl, sourceCounts, triggerReasonCounts, {
  width = 400, margin = { top: 20, right: 40, bottom: 30, left: 130 },
  t = (k) => k,
} = {}) {
  if (!svgEl) return;
  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();

  // Build combined data
  const sourceData = [];
  if (sourceCounts && Object.keys(sourceCounts).length > 0) {
    Object.entries(sourceCounts).forEach(([key, val]) => {
      sourceData.push({ label: t('dashboard.source.' + key, { defaultValue: key }), value: val, group: 'source' });
    });
  }
  const triggerData = [];
  if (triggerReasonCounts && Object.keys(triggerReasonCounts).length > 0) {
    Object.entries(triggerReasonCounts).forEach(([key, val]) => {
      triggerData.push({ label: key, value: val, group: 'trigger' });
    });
  }
  const allData = [...sourceData, ...triggerData];
  if (allData.length === 0) return;

  const barHeight = 26;
  const sectionGap = sourceData.length > 0 && triggerData.length > 0 ? 24 : 0;
  const totalHeight = margin.top + margin.bottom + allData.length * (barHeight + 6) + sectionGap;

  svg.attr('width', width).attr('height', totalHeight);

  const innerW = width - margin.left - margin.right;
  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

  const maxVal = d3.max(allData, d => d.value) || 1;
  const x = d3.scaleLinear().domain([0, maxVal]).nice().range([0, innerW]);

  let yOffset = 0;

  // Source section
  if (sourceData.length > 0) {
    g.append('text').attr('x', -margin.left + 4).attr('y', yOffset - 4)
      .style('font-size', '12px').style('fill', 'var(--text-muted, #777)').style('font-weight', '600')
      .text(t('dashboard.chart.sourceHeader'));

    sourceData.forEach((d, i) => {
      const by = yOffset + i * (barHeight + 6);
      g.append('text').attr('x', -4).attr('y', by + barHeight / 2).attr('dy', '0.35em')
        .attr('text-anchor', 'end').style('font-size', '13px').style('fill', 'var(--text, #333)')
        .text(d.label);
      g.append('rect').attr('x', 0).attr('y', by).attr('width', x(d.value)).attr('height', barHeight)
        .attr('rx', 3).attr('fill', CHART_COLORS[i % CHART_COLORS.length]);
      g.append('text').attr('x', x(d.value) + 6).attr('y', by + barHeight / 2).attr('dy', '0.35em')
        .style('font-size', '13px').style('font-weight', '600').style('fill', 'var(--text, #333)')
        .text(d.value);
    });
    yOffset += sourceData.length * (barHeight + 6) + sectionGap;
  }

  // Trigger section
  if (triggerData.length > 0) {
    g.append('text').attr('x', -margin.left + 4).attr('y', yOffset - 4)
      .style('font-size', '12px').style('fill', 'var(--text-muted, #777)').style('font-weight', '600')
      .text(t('dashboard.chart.escalationHeader'));

    triggerData.forEach((d, i) => {
      const by = yOffset + i * (barHeight + 6);
      g.append('text').attr('x', -4).attr('y', by + barHeight / 2).attr('dy', '0.35em')
        .attr('text-anchor', 'end').style('font-size', '13px').style('fill', 'var(--text, #333)')
        .text(d.label);
      g.append('rect').attr('x', 0).attr('y', by).attr('width', x(d.value)).attr('height', barHeight)
        .attr('rx', 3).attr('fill', CHART_COLORS[(sourceData.length + i) % CHART_COLORS.length]);
      g.append('text').attr('x', x(d.value) + 6).attr('y', by + barHeight / 2).attr('dy', '0.35em')
        .style('font-size', '13px').style('font-weight', '600').style('fill', 'var(--text, #333)')
        .text(d.value);
    });
  }
}

// ==================== Main Component ====================

function TeacherDashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // --- State ---
  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedQuestionCategory, setSelectedQuestionCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allSummaries, setAllSummaries] = useState({});
  const [classOverviewLoading, setClassOverviewLoading] = useState(false);
  // P1-2: AI分析の品質指標 (chart routing telemetry aggregate)
  // P3 hotfix: distinguish initial / loading (null + no error) from fetch failure (error=true)
  const [modelABSummary, setModelABSummary] = useState(null);
  const [modelABSummaryError, setModelABSummaryError] = useState(false);
  // T5: DBR (Design-Based Research) 形成的評価ダッシュボード (2026-07-11 plan P1-1(C))
  // 同じ loading/error/success 3-state パターン踏襲: null+no-error=loading, error=true=fetch失敗
  // (distinct from loading — a failure must never render identically to "読み込み中…")
  const [dbrSummary, setDbrSummary] = useState(null);
  const [dbrSummaryError, setDbrSummaryError] = useState(false);

  const LIMIT = 50;

  // D3 chart refs - class overview
  const classGraphTypeRef = useRef(null);
  const classProgressRef = useRef(null);
  const classStyleRef = useRef(null);
  const classCognitiveRef = useRef(null);

  // D3 chart refs - individual student panels
  const studentGraphTypeRef = useRef(null);
  const studentOperationRef = useRef(null);

  // --- Role check ---
  useEffect(() => {
    authAPI.getCurrentUser().then((res) => {
      const user = res.data;
      if (!user || user.role !== 'teacher') {
        navigate('/dashboard', { replace: true });
      }
    }).catch(() => {
      navigate('/dashboard', { replace: true });
    });
  }, [navigate]);

  // --- Fetch students ---
  const fetchStudents = useCallback(async (newOffset = 0, append = false) => {
    try {
      setLoading(true);
      setError(null);
      const res = await teacherAPI.getAnalyticsStudents(newOffset, LIMIT);
      const data = res.data;
      if (append) {
        setStudents((prev) => [...prev, ...(data.students || [])]);
      } else {
        setStudents(data.students || []);
      }
      setTotalStudents(data.total || 0);
      setOffset(newOffset);
    } catch (err) {
      logClientError('Failed to fetch students:', err);
      setError('dashboard.teacher.fetchStudentsFailed');
      if (!append) setStudents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents(0);
  }, [fetchStudents]);

  // --- P1-2: Fetch class-level AI model AB summary (chart routing telemetry) ---
  useEffect(() => {
    teacherAPI.getModelABSummary()
      .then((r) => {
        setModelABSummary(r.data);
        setModelABSummaryError(false);
      })
      .catch((err) => {
        // P3 hotfix: set error flag so render can show a distinct failure state
        // instead of permanently showing "読み込み中…" (which is the initial-null state).
        logClientError('Failed to load model AB summary:', err);
        setModelABSummaryError(true);
      });
  }, []);

  // --- T5: Fetch class-level DBR (Design-Based Research) formative-review summary ---
  useEffect(() => {
    analyticsAPI.getDbrSummary()
      .then((r) => {
        // Defensive: a malformed / partial 200 must render the error state, NEVER
        // crash the whole dashboard. The success branch directly dereferences
        // students[].weekly_lv4, class_aggregates.weekly_mechanism_usage (arrays) and
        // trigger_reason_distribution / effective_support_level_distribution (objects,
        // via Object.keys/entries), so ALL of them must be validated (Codex 2026-07-11
        // P2 — a top-level array alone is not enough). Treat any non-conforming body as
        // a failure.
        const d = r?.data;
        const ca = d?.class_aggregates;
        const isObj = (o) => o != null && typeof o === 'object' && !Array.isArray(o);
        // React crashes ("Objects are not valid as a React child") if any RENDERED leaf
        // is a non-primitive, so validate the TYPE of every value the success branch
        // renders, not just container shapes (Codex 2026-07-11 P2 rounds 1-3: a
        // top-level array, a `[null]` element, and a `{week, count: {}}` leaf each
        // slipped past progressively-looser guards). Any non-conforming body -> error
        // state, never the success branch.
        const isStr = (v) => typeof v === 'string';
        const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
        // arr of {week: string, ...numFields all finite numbers}
        const weekArrOk = (arr, numFields) =>
          Array.isArray(arr)
          && arr.every((w) => isObj(w) && isStr(w.week) && numFields.every((f) => isNum(w[f])));
        // a distribution object: every value is a finite number (keys are rendered as
        // strings, values as counts).
        const distOk = (o) => isObj(o) && Object.values(o).every(isNum);
        const ok = d
          && Array.isArray(d.students)
          && d.students.every((s) => isObj(s) && isStr(s.username) && weekArrOk(s.weekly_lv4, ['count']))
          && isObj(ca)
          && weekArrOk(ca.weekly_mechanism_usage, ['stage_change', 'keyword_click', 'graph_element_click'])
          && distOk(ca.trigger_reason_distribution)
          && distOk(ca.effective_support_level_distribution);
        if (!ok) {
          console.error('DBR summary response has an unexpected shape:', d);
          setDbrSummaryError(true);
          return;
        }
        setDbrSummary(d);
        setDbrSummaryError(false);
      })
      .catch((err) => {
        // Same P3-hotfix pattern as model AB summary: error must render as a
        // distinct failure state, never fall through as an indefinite loading state.
        logClientError('Failed to load DBR summary:', err);
        setDbrSummaryError(true);
      });
  }, []);

  // --- Fetch all student summaries for class overview ---
  useEffect(() => {
    if (students.length === 0) return;

    const fetchAllSummaries = async () => {
      setClassOverviewLoading(true);
      const summaries = {};
      const batchSize = 10;
      for (let i = 0; i < students.length; i += batchSize) {
        const batch = students.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(s => teacherAPI.getStudentSummary(s.id))
        );
        results.forEach((result, idx) => {
          if (result.status === 'fulfilled') {
            summaries[batch[idx].id] = result.value.data;
          }
        });
      }
      setAllSummaries(summaries);
      setClassOverviewLoading(false);
    };

    fetchAllSummaries();
  }, [students]);

  // --- Aggregate class overview data ---
  const classOverviewData = React.useMemo(() => {
    if (Object.keys(allSummaries).length === 0) {
      return { graphTypes: [], progress: [], styles: [], cognitive: [], faq: [] };
    }

    // 1. よく使ったグラフの種類（操作の回数） (action_counts_by_graph_type; counts actions, not difficulty)
    const graphTypeAgg = {};
    Object.values(allSummaries).forEach(s => {
      const counts = s.action_counts_by_graph_type || s.action_counts_by_type;
      if (counts) {
        Object.entries(counts).forEach(([type, count]) => {
          const label = t('graphType.' + type, { defaultValue: type });
          graphTypeAgg[label] = (graphTypeAgg[label] || 0) + count;
        });
      }
    });
    const graphTypes = Object.entries(graphTypeAgg)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    // 2. 最近の学習進捗 (sessions by date, no specific datetime shown)
    const dateAgg = {};
    Object.values(allSummaries).forEach(s => {
      if (s.sessions) {
        s.sessions.forEach(session => {
          if (session.created_at) {
            const dateKey = new Date(session.created_at).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US', {
              month: '2-digit', day: '2-digit',
            });
            dateAgg[dateKey] = (dateAgg[dateKey] || 0) + 1;
          }
        });
      }
    });
    const progress = Object.entries(dateAgg)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => {
        const [am, ad] = a.label.split('/').map(Number);
        const [bm, bd] = b.label.split('/').map(Number);
        return (am * 100 + ad) - (bm * 100 + bd);
      })
      .slice(-14);

    // 3. 支援レベル分布
    const styleCounts = { 'Lv1': 0, 'Lv2': 0, 'Lv3': 0, 'Lv4': 0 };
    Object.values(allSummaries).forEach(s => {
      if (s.support_level_distribution) {
        Object.entries(s.support_level_distribution).forEach(([level, count]) => {
          const key = `Lv${level}`;
          if (styleCounts[key] !== undefined) {
            styleCounts[key] += count;
          }
        });
      }
    });
    const styles = Object.entries(styleCounts)
      .map(([key, value]) => ({ label: t('dashboard.supportLevel.' + key, { defaultValue: key }), value }))
      .filter(d => d.value > 0);

    // 4. エスカレーション要因分布
    const cogAgg = {};
    Object.values(allSummaries).forEach(s => {
      if (s.escalation_history) {
        s.escalation_history.forEach(e => {
          const reason = triggerReasonLabel(e.trigger_reason, t);
          cogAgg[reason] = (cogAgg[reason] || 0) + 1;
        });
      }
    });
    const cognitive = Object.entries(cogAgg)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    // 5. よくある質問カテゴリ
    const faqAgg = {};
    Object.values(allSummaries).forEach(s => {
      if (s.common_question_categories) {
        s.common_question_categories.forEach(({ category, count }) => {
          faqAgg[category] = (faqAgg[category] || 0) + count;
        });
      }
    });
    const faq = Object.entries(faqAgg)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    return { graphTypes, progress, styles, cognitive, faq };
  }, [allSummaries, t, i18n.language]);

  // Why the class summary cannot be exported yet (null = ready). The file says
  // "class-wide", so a partial snapshot (more students than loaded, a failed
  // summary, DBR totals missing) must not be offered.
  const exportBlocker = (() => {
    if (classOverviewLoading) return 'loading';
    if (students.length === 0) return 'noStudents';
    if (students.length < totalStudents) return 'notAllLoaded';
    if (Object.keys(allSummaries).length < students.length) return 'summaryFailed';
    if (dbrSummaryError) return 'dbrFailed';
    if (dbrSummary === null) return 'loading';
    return null;
  })();

  // Class-level aggregates only (see utils/teacherExport.js).
  const handleExportClassSummary = useCallback(() => {
    const rows = buildClassSummaryRows(classOverviewData, dbrSummary, t);
    const day = new Date();
    const stamp = `${day.getFullYear()}${String(day.getMonth() + 1).padStart(2, '0')}${String(day.getDate()).padStart(2, '0')}`;
    downloadCsv(`${t('dashboard.teacher.export.filePrefix')}-${stamp}.csv`, rowsToCsv(rows));
  }, [classOverviewData, dbrSummary, t]);

  // --- Draw D3 charts for class overview ---
  useEffect(() => {
    if (classOverviewData.graphTypes.length > 0 && classGraphTypeRef.current) {
      drawHorizontalBarChart(classGraphTypeRef.current, classOverviewData.graphTypes);
    }
  }, [classOverviewData.graphTypes]);

  useEffect(() => {
    if (classOverviewData.progress.length > 0 && classProgressRef.current) {
      drawLineChart(classProgressRef.current, classOverviewData.progress, { t });
    }
  }, [classOverviewData.progress, t]);

  useEffect(() => {
    if (classOverviewData.styles.length > 0 && classStyleRef.current) {
      drawHorizontalBarChart(classStyleRef.current, classOverviewData.styles);
    }
  }, [classOverviewData.styles]);

  useEffect(() => {
    if (classOverviewData.cognitive.length > 0 && classCognitiveRef.current) {
      drawHorizontalBarChart(classCognitiveRef.current, classOverviewData.cognitive);
    }
  }, [classOverviewData.cognitive]);

  // --- Draw D3 charts for individual student panels ---
  useEffect(() => {
    if (!summary) return;

    // Panel 1: よく使ったグラフの種類（操作の回数）
    if (studentGraphTypeRef.current) {
      const counts = summary.action_counts_by_graph_type || summary.action_counts_by_type;
      if (counts && Object.keys(counts).length > 0) {
        const data = Object.entries(counts)
          .map(([type, value]) => ({ label: t('graphType.' + type, { defaultValue: type }), value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);
        drawHorizontalBarChart(studentGraphTypeRef.current, data);
      } else {
        d3.select(studentGraphTypeRef.current).selectAll('*').remove();
      }
    }

    // Panel 5: 常見問題 — React renderのため useEffect 不要（JSXで直接描画）
  }, [summary, t]);

  // --- Fetch student summary ---
  const handleSelectStudent = useCallback(async (student) => {
    setSelectedStudent(student);
    setSummary(null);
    setSummaryLoading(true);
    try {
      if (allSummaries[student.id]) {
        setSummary(allSummaries[student.id]);
      } else {
        const res = await teacherAPI.getStudentSummary(student.id);
        setSummary(res.data);
      }
    } catch (err) {
      logClientError('Failed to fetch student summary:', err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [allSummaries]);

  // --- Load more ---
  const handleLoadMore = () => {
    const newOffset = offset + LIMIT;
    fetchStudents(newOffset, true);
  };

  // --- Render empty chart placeholder ---
  const renderEmptyChart = (message) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '120px', color: 'var(--text-muted)', fontSize: '14px',
    }}>
      {message}
    </div>
  );

  // --- Render data source label ---
  const renderDataSource = (text) => (
    <p style={{
      fontSize: '12px', color: 'var(--text-muted, #999)', marginTop: '12px',
      fontStyle: 'italic', borderTop: '1px solid var(--border, #eee)', paddingTop: '8px',
    }}>
      {text}
    </p>
  );

  // --- Panel 2: 最近の進度 ---
  const renderRecentProgress = (sessions) => {
    if (!sessions || sessions.length === 0) {
      return renderEmptyChart(t('dashboard.teacher.noSessionHistory'));
    }

    // Group by graph_type and count actions
    const typeMap = {};
    sessions.forEach(s => {
      const gt = s.graph_type;
      const label = gt ? t('graphType.' + gt, { defaultValue: gt }) : t('dashboard.teacher.uncategorized');
      if (!typeMap[label]) {
        typeMap[label] = { count: 0, actions: 0 };
      }
      typeMap[label].count += 1;
      typeMap[label].actions += (s.action_count || 0);
    });

    const entries = Object.entries(typeMap).sort((a, b) => b[1].count - a[1].count);

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%', borderCollapse: 'collapse', fontSize: '14px',
        }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border, #ddd)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)' }}>{t('dashboard.teacher.colGraphType')}</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--text-muted)' }}>{t('dashboard.teacher.colSessions')}</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--text-muted)' }}>{t('dashboard.teacher.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([label, info], idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                <td style={{ padding: '8px 12px' }}>{label}</td>
                <td style={{ textAlign: 'center', padding: '8px 12px' }}>{info.count}</td>
                <td style={{ textAlign: 'center', padding: '8px 12px' }}>{info.actions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // --- Panel 3: 推定学習スタイル ---
  const renderLearningStyle = (ls) => {
    if (!ls) {
      return renderEmptyChart(t('dashboard.teacher.noDataInsufficient'));
    }

    const confidence = ls.confidence;
    const axes = ['perception', 'input', 'processing', 'understanding'];

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        {axes.map(axis => {
          const val = ls[axis];
          if (val === undefined || val === null) return null;
          return (
            <div key={axis} style={{
              padding: '14px', borderRadius: '8px',
              background: 'var(--warm-bg, #faf5ee)', border: '1px solid var(--border, #e0d5c7)',
            }}>
              <div style={{
                fontSize: '12px', color: 'var(--text-muted, #888)', marginBottom: '6px', fontWeight: 600,
              }}>
                {t('dashboard.learningStyle.' + axis, { defaultValue: axis })}
              </div>
              <div style={{
                fontSize: '16px', fontWeight: 700, color: 'var(--primary, #5a7d8c)',
              }}>
                {val}
              </div>
            </div>
          );
        })}
        {confidence !== undefined && confidence !== null && (
          <div style={{
            gridColumn: '1 / -1', textAlign: 'center',
            padding: '8px', fontSize: '14px', color: 'var(--text-muted)',
          }}>
            {t('dashboard.teacher.confidence')}: <strong>{typeof confidence === 'number' ? `${Math.round(confidence * 100)}%` : confidence}</strong>
          </div>
        )}
      </div>
    );
  };

  // --- Panel 4: 推定認知困難タイプ ---
  const renderCognitiveDifficulty = (cd) => {
    if (!cd) {
      return renderEmptyChart(t('dashboard.teacher.noData'));
    }

    const keys = COGNITIVE_KEYS;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {keys.map(key => {
          const val = cd[key];
          if (val === undefined || val === null) return null;
          const numVal = typeof val === 'number' ? val : 0;
          const clampedVal = Math.min(Math.max(numVal, 0), 3);
          const severity = Math.round(clampedVal);
          const pct = (clampedVal / 3) * 100;

          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                width: '120px', flexShrink: 0, fontSize: '13px',
                color: 'var(--text, #333)', textAlign: 'right',
              }}>
                {t('dashboard.cog.' + key)}
              </span>
              <div style={{
                flex: 1, height: '18px', background: 'var(--bg-muted, #f0f0f0)',
                borderRadius: '9px', overflow: 'hidden', position: 'relative',
              }}>
                <div style={{
                  width: `${pct}%`, height: '100%', borderRadius: '9px',
                  background: SEVERITY_COLORS[severity] || SEVERITY_COLORS[0],
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <span style={{
                width: '30px', textAlign: 'center', fontSize: '14px', fontWeight: 600,
                color: SEVERITY_COLORS[severity] || '#333',
              }}>
                {typeof val === 'number' ? val.toFixed(1) : val}
              </span>
            </div>
          );
        })}
        <div style={{
          display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '8px', fontSize: '12px',
        }}>
          {[
            { n: 0, key: 'none', color: SEVERITY_COLORS[0] },
            { n: 1, key: 'mild', color: SEVERITY_COLORS[1] },
            { n: 2, key: 'moderate', color: SEVERITY_COLORS[2] },
            { n: 3, key: 'severe', color: SEVERITY_COLORS[3] },
          ].map(item => (
            <span key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{
                width: '12px', height: '12px', borderRadius: '50%', background: item.color,
                display: 'inline-block',
              }} />
              {item.n}: {t('dashboard.severity.' + item.key)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="teacher-dashboard">
      {/* Header */}
      <header className="teacher-dashboard-header">
        <h1 style={{ fontSize: '1.6em' }}>{t('dashboard.teacher.pageTitle')}</h1>
        <nav style={{ display: 'flex', gap: '8px' }}>
          <Link to="/dashboard" className="btn-secondary" style={{ fontSize: '1em', padding: '8px 18px' }}>
            {t('dashboard.teacher.backToDashboard')}
          </Link>
        </nav>
      </header>

      {/* Class Overview Section */}
      <section className="class-overview-section">
        <h2 style={{ fontSize: '1.3em', color: 'var(--primary)', marginBottom: '16px', fontWeight: 700 }}>
          {t('dashboard.teacher.classOverview')}
          <span style={{ fontSize: '0.65em', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '12px' }}>
            {t('dashboard.teacher.studentCount', { count: students.length })}
          </span>
        </h2>
        {/* Only a COMPLETE class snapshot is exported: every student page loaded, every
            summary fetched, and the DBR totals in. Otherwise say why (Codex D-R1). */}
        {exportBlocker && exportBlocker !== 'loading' && (
          <p role="status" style={{ fontSize: '0.8em', color: 'var(--text-muted)', margin: '0 0 16px' }}>
            {t('dashboard.teacher.export.blocked.' + exportBlocker)}
          </p>
        )}
        {!exportBlocker && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleExportClassSummary}
              style={{ fontSize: '0.9em', padding: '6px 14px' }}
            >
              {t('dashboard.teacher.export.button')}
            </button>
            <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{t('dashboard.teacher.export.note')}</span>
          </div>
        )}

        {classOverviewLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: '12px', color: 'var(--text-muted)', fontSize: '14px' }}>{t('dashboard.teacher.aggregating')}</p>
          </div>
        ) : (
          <div className="class-overview-grid">
            {/* Panel 1: よく使ったグラフの種類（操作の回数） */}
            <div className="class-overview-panel card">
              <h3 style={{ fontSize: '1.1em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                {t('dashboard.teacher.weakGraphTypes')}
              </h3>
              <PanelHelp text={t('dashboard.teacher.help.graphTypes')} toggleLabel={t('dashboard.teacher.help.toggle')} />
              {classOverviewData.graphTypes.length > 0 ? (
                <svg data-chart-root="" ref={classGraphTypeRef} style={{ width: '100%', maxWidth: '420px' }} />
              ) : renderEmptyChart(t('dashboard.teacher.noData'))}
            </div>

            {/* Panel 2: 最近の学習進捗 */}
            <div className="class-overview-panel card">
              <h3 style={{ fontSize: '1.1em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                {t('dashboard.teacher.recentProgress')}
              </h3>
              <PanelHelp text={t('dashboard.teacher.help.progressClass')} toggleLabel={t('dashboard.teacher.help.toggle')} />
              {classOverviewData.progress.length > 0 ? (
                <svg data-chart-root="" ref={classProgressRef} style={{ width: '100%', maxWidth: '420px' }} />
              ) : renderEmptyChart(t('dashboard.teacher.noData'))}
            </div>

            {/* Panel 3: 支援レベル分布 */}
            <div className="class-overview-panel card">
              <h3 style={{ fontSize: '1.1em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                {t('dashboard.teacher.supportLevelDistClass')}
              </h3>
              <PanelHelp text={t('dashboard.teacher.help.supportLevel')} toggleLabel={t('dashboard.teacher.help.toggle')} />
              {classOverviewData.styles.length > 0 ? (
                <svg data-chart-root="" ref={classStyleRef} style={{ width: '100%', maxWidth: '420px' }} />
              ) : renderEmptyChart(t('dashboard.teacher.noData'))}
            </div>

            {/* Panel 4: エスカレーション要因分布 */}
            <div className="class-overview-panel card">
              <h3 style={{ fontSize: '1.1em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                {t('dashboard.teacher.escalationDist')}
              </h3>
              <PanelHelp text={t('dashboard.teacher.help.escalation')} toggleLabel={t('dashboard.teacher.help.toggle')} />
              {classOverviewData.cognitive.length > 0 ? (
                <svg data-chart-root="" ref={classCognitiveRef} style={{ width: '100%', maxWidth: '420px' }} />
              ) : renderEmptyChart(t('dashboard.teacher.noData'))}
            </div>

            {/* Panel 5: 常見問題 */}
            <div className="class-overview-panel card" style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: '1.1em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                {t('dashboard.teacher.faqCategories')}
              </h3>
              <PanelHelp text={t('dashboard.teacher.help.faq')} toggleLabel={t('dashboard.teacher.help.toggle')} />
              {classOverviewData.faq.length > 0 ? (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {classOverviewData.faq.map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '8px 16px', background: 'var(--warm-bg, #faf5ee)',
                      borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border)',
                    }}>
                      <span style={{ fontSize: '1.05em', fontWeight: 600, color: CHART_COLORS[idx % CHART_COLORS.length] }}>
                        {item.value}
                      </span>
                      <span style={{ fontSize: '14px', color: 'var(--text)' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              ) : renderEmptyChart(t('dashboard.teacher.noData'))}
            </div>

            {/* Panel 6: AI分析の品質指標 (P1-2 v2.14.4-p1-2-model-ab-log) */}
            {/*
              Contracts enforced in this panel:
              - Modelname-free: 具体的なモデル名 / 内部識別子は一切表示しない。
                「primary」「fallback」「verified」など抽象語のみで表現する。
              - Privacy: 生徒個別 user_id は表示しない (担当生徒のみ集計)。
              - CUD ladder reuse: 既存 --success / --warning / --text-muted token のみ使用。
                新規 color token 追加禁止。
            */}
            <div className="class-overview-panel card" style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: '1.1em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                {t('dashboard.teacher.aiQualityMetrics')}
              </h3>
              <PanelHelp text={t('dashboard.teacher.help.aiQuality')} toggleLabel={t('dashboard.teacher.help.toggle')} />
              {modelABSummaryError ? (
                // P3 hotfix: distinct error state — previously a fetch failure
                // also set modelABSummary=null, which rendered identical to the
                // initial loading state ("読み込み中…" forever).
                <div style={{ color: 'var(--danger)', fontStyle: 'italic' }} role="alert">
                  {t('dashboard.teacher.metricsLoadFailed')}
                </div>
              ) : modelABSummary === null ? (
                <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {t('common.loading')}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  {/* 指標 1: 一次通過率 */}
                  <div style={{ borderLeft: '3px solid var(--success)', paddingLeft: '12px' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginBottom: '4px' }}>
                      {t('dashboard.teacher.primaryPassRate')}
                    </div>
                    <div style={{ fontSize: '1.8em', fontWeight: 700, color: 'var(--success)' }}>
                      {(modelABSummary.primary_pass_rate * 100).toFixed(1)}%
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75em', marginTop: '4px' }}>
                      {t('dashboard.teacher.tracesCount', { n: modelABSummary.primary_pass_count, total: modelABSummary.total_traces })}
                    </div>
                  </div>

                  {/* 指標 2: フォールバック理由の内訳 (4-code 公開契約: schema_invalid / low_confidence / timeout / api_error) */}
                  <div style={{ borderLeft: '3px solid var(--warning)', paddingLeft: '12px' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginBottom: '8px' }}>
                      {t('dashboard.teacher.fallbackBreakdown')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {['schema_invalid', 'low_confidence', 'timeout', 'api_error'].map((key) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em' }}>
                          <span>{t('dashboard.fallbackReason.' + key)}</span>
                          <span style={{ fontWeight: 600 }}>
                            {(modelABSummary.fallback_reason_counts && modelABSummary.fallback_reason_counts[key]) || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 指標 3: 教師の人工修正率 (Phase 1 placeholder, Condition C6 hard-string) */}
                  <div style={{ borderLeft: '3px dashed var(--text-muted)', paddingLeft: '12px' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginBottom: '4px' }}>
                      {t('dashboard.teacher.manualCorrectionRate')}
                    </div>
                    <div style={{ fontSize: '1em', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '12px' }}>
                      {t('dashboard.teacher.collectingData')}
                    </div>
                  </div>
                </div>
              )}
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75em', marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
                {t('dashboard.teacher.sourceChartRouting')}
              </div>
            </div>

            {/* Panel 7: DBR (Design-Based Research) 形成的評価 (T5 2026-07-11 plan P1-1(C)) */}
            {/*
              Contracts enforced in this panel:
              - Advisory only: handoff_advisory is a mechanical reference indicator,
                never an automated handoff trigger (研究計画書 v4 §6.3 運用手順(1)-(3)
                approximation). The disclaimer text + badge wording must always say
                "advisory / 参考指標" and never claim an automated decision/action.
              - Privacy: DbrSummaryResponse already excludes email / session titles /
                dialogue content / exact timestamps — this panel renders exactly what
                the response contains and adds no additional identifying data.
              - CUD ladder reuse: only existing --success / --warning / --danger /
                --text-muted / --primary / --border tokens are used; no new color
                tokens introduced.
              - loading/error/success states are visually and structurally distinct
                (same P3-hotfix null+error dual-flag pattern as Panel 6 above) so a
                fetch failure never renders identically to the loading state.
            */}
            <div className="class-overview-panel card" style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: '1.1em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                {t('dashboard.teacher.dbrPanelTitle')}
              </h3>
              <PanelHelp text={t('dashboard.teacher.help.dbr')} toggleLabel={t('dashboard.teacher.help.toggle')} />
              {dbrSummaryError ? (
                <div style={{ color: 'var(--danger)', fontStyle: 'italic' }} role="alert">
                  {t('dashboard.teacher.dbrLoadFailed')}
                </div>
              ) : dbrSummary === null ? (
                <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {t('common.loading')}
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 0, marginBottom: '14px', fontStyle: 'italic' }}>
                    {t('dashboard.teacher.dbrAdvisoryDisclaimer')}
                  </p>

                  {/* 生徒別: 週次 Lv4 到達数 + advisory バッジ */}
                  {dbrSummary.students.length === 0 ? renderEmptyChart(t('dashboard.teacher.noData')) : (
                    <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border, #ddd)' }}>
                            <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)' }}>
                              {t('dashboard.teacher.colStudent')}
                            </th>
                            {(dbrSummary.students[0]?.weekly_lv4 || []).map((w) => (
                              <th key={w.week} style={{ textAlign: 'center', padding: '8px 6px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 400 }}>
                                {w.week}
                              </th>
                            ))}
                            <th style={{ textAlign: 'center', padding: '8px 12px', color: 'var(--text-muted)' }}>
                              {t('dashboard.teacher.dbrAdvisoryCol')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {dbrSummary.students.map((s) => (
                            <tr key={s.student_id} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                              <td style={{ padding: '8px 12px' }}>{s.username}</td>
                              {s.weekly_lv4.map((w) => (
                                <td key={w.week} style={{
                                  textAlign: 'center', padding: '8px 6px',
                                  fontWeight: w.count > 0 ? 600 : 400,
                                  color: w.count > 0 ? 'var(--warning)' : 'var(--text-muted)',
                                }}>
                                  {w.count}
                                </td>
                              ))}
                              <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                                {s.handoff_advisory ? (
                                  <span style={{
                                    display: 'inline-block', padding: '2px 10px', borderRadius: '999px',
                                    border: '1px solid var(--warning)', color: 'var(--warning)',
                                    fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                                  }}>
                                    {t('dashboard.teacher.dbrAdvisoryBadge')}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)' }}>—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* クラス集計: 機構利用状況 / エスカレーション要因分布 / 実効支援レベル分布 */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                    {/* 支援機構の利用状況（週次） */}
                    <div>
                      <h4 style={{ fontSize: '0.95em', color: 'var(--text)', marginBottom: '8px', fontWeight: 600 }}>
                        {t('dashboard.teacher.dbrMechanismUsage')}
                      </h4>
                      {dbrSummary.class_aggregates.weekly_mechanism_usage.every(
                        (w) => w.stage_change === 0 && w.keyword_click === 0 && w.graph_element_click === 0
                      ) ? renderEmptyChart(t('dashboard.teacher.noData')) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid var(--border, #ddd)' }}>
                                <th style={{ padding: '4px 6px' }} />
                                {['stage_change', 'keyword_click', 'graph_element_click'].map((key) => (
                                  <th key={key} style={{ textAlign: 'center', padding: '4px 6px', color: 'var(--text-muted)', fontWeight: 400 }}>
                                    {t('dashboard.teacher.dbrMechanism.' + key)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {dbrSummary.class_aggregates.weekly_mechanism_usage.map((w) => (
                                <tr key={w.week} style={{ borderBottom: '1px solid var(--border, #f0f0f0)' }}>
                                  <td style={{ padding: '4px 6px', color: 'var(--text-muted)' }}>{w.week}</td>
                                  <td style={{ textAlign: 'center', padding: '4px 6px' }}>{w.stage_change}</td>
                                  <td style={{ textAlign: 'center', padding: '4px 6px' }}>{w.keyword_click}</td>
                                  <td style={{ textAlign: 'center', padding: '4px 6px' }}>{w.graph_element_click}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* エスカレーション要因の分布 (allowlist正規化済み: 8認知軸 + other) */}
                    <div>
                      <h4 style={{ fontSize: '0.95em', color: 'var(--text)', marginBottom: '8px', fontWeight: 600 }}>
                        {t('dashboard.teacher.dbrTriggerDistribution')}
                      </h4>
                      {Object.keys(dbrSummary.class_aggregates.trigger_reason_distribution).length === 0 ? (
                        renderEmptyChart(t('dashboard.teacher.noData'))
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {Object.entries(dbrSummary.class_aggregates.trigger_reason_distribution)
                            .sort((a, b) => b[1] - a[1])
                            .map(([reason, count]) => (
                              <div key={reason} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em' }}>
                                <span>{reason === 'other' ? t('dashboard.teacher.dbrOtherLabel') : t('dashboard.cog.' + reason, { defaultValue: reason })}</span>
                                <span style={{ fontWeight: 600 }}>{count}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>

                    {/* 実効支援レベルの分布 */}
                    <div>
                      <h4 style={{ fontSize: '0.95em', color: 'var(--text)', marginBottom: '8px', fontWeight: 600 }}>
                        {t('dashboard.teacher.dbrSupportLevelDistribution')}
                      </h4>
                      {Object.keys(dbrSummary.class_aggregates.effective_support_level_distribution).length === 0 ? (
                        renderEmptyChart(t('dashboard.teacher.noData'))
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {Object.entries(dbrSummary.class_aggregates.effective_support_level_distribution)
                            .sort((a, b) => Number(a[0]) - Number(b[0]))
                            .map(([level, count]) => (
                              <div key={level} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em' }}>
                                <span>{t('dashboard.supportLevel.Lv' + level, { defaultValue: 'Lv' + level })}</span>
                                <span style={{ fontWeight: 600 }}>{count}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75em', marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
                {t('dashboard.teacher.dbrSourceNote')}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Student List + Detail Section */}
      <div className="teacher-dashboard-body">
        {/* Sidebar: Student List */}
        <aside className="teacher-dashboard-sidebar">
          <h2 style={{ fontSize: '1.15em', marginBottom: '12px', color: 'var(--primary)' }}>{t('dashboard.teacher.studentList')}</h2>

          {error && <p style={{ color: 'var(--danger)', fontSize: '14px' }}>{t(error)}</p>}

          {loading && students.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : students.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{t('dashboard.teacher.noStudents')}</p>
          ) : (
            <>
              <ul className="student-list">
                {students.map((s) => (
                  <li
                    key={s.id}
                    className={`student-list-item ${selectedStudent?.id === s.id ? 'selected' : ''}`}
                    onClick={() => handleSelectStudent(s)}
                  >
                    <div className="student-list-name">{s.username}</div>
                    <div className="student-list-meta">
                      <span>{t('dashboard.teacher.sessionLabel', { n: s.session_count ?? '-' })}</span>
                    </div>
                  </li>
                ))}
              </ul>

              {students.length < totalStudents && (
                <button
                  className="btn-secondary"
                  onClick={handleLoadMore}
                  disabled={loading}
                  style={{ width: '100%', marginTop: '8px', fontSize: '14px' }}
                >
                  {loading ? t('common.loading') : t('dashboard.teacher.loadMore')}
                </button>
              )}
            </>
          )}
        </aside>

        {/* Main: Student Detail - 5 Panels */}
        <main className="teacher-dashboard-main">
          {!selectedStudent ? (
            <div className="teacher-dashboard-empty">
              <p style={{ fontSize: '14px' }}>{t('dashboard.teacher.selectStudentPrompt')}</p>
            </div>
          ) : summaryLoading ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
              <p style={{ marginTop: '12px', color: 'var(--text-muted)', fontSize: '14px' }}>{t('common.loading')}</p>
            </div>
          ) : (
            <div className="teacher-dashboard-panels">
              <h2 style={{ fontSize: '1.2em', marginBottom: '16px', color: 'var(--primary)' }}>
                {t('dashboard.teacher.studentAnalysis', { name: selectedStudent.username })}
              </h2>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px',
              }}>
                {/* Panel 1: よく使ったグラフの種類（操作の回数） */}
                <section className="dashboard-panel card">
                  <h3 style={{ fontSize: '1.05em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                    {t('dashboard.teacher.weakGraphTypes')}
                  </h3>
                  <PanelHelp text={t('dashboard.teacher.help.graphTypes')} toggleLabel={t('dashboard.teacher.help.toggle')} />
                  {(() => {
                    const counts = summary?.action_counts_by_graph_type || summary?.action_counts_by_type;
                    if (!counts || Object.keys(counts).length === 0) {
                      return renderEmptyChart(t('dashboard.teacher.noData'));
                    }
                    return <svg data-chart-root="" ref={studentGraphTypeRef} style={{ width: '100%', maxWidth: '420px' }} />;
                  })()}
                  {renderDataSource(t('dashboard.teacher.sourceActionLog'))}
                </section>

                {/* Panel 2: 最近の進度 */}
                <section className="dashboard-panel card">
                  <h3 style={{ fontSize: '1.05em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                    {t('dashboard.teacher.recentProgressShort')}
                  </h3>
                  <PanelHelp text={t('dashboard.teacher.help.progressStudent')} toggleLabel={t('dashboard.teacher.help.toggle')} />
                  {renderRecentProgress(summary?.sessions)}
                  {renderDataSource(t('dashboard.teacher.sourceRecentSessions'))}
                </section>

                {/* Panel 3: 推定学習スタイル */}
                <section className="dashboard-panel card">
                  <h3 style={{ fontSize: '1.05em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                    {t('dashboard.teacher.estLearningStyle')}
                  </h3>
                  <PanelHelp text={t('dashboard.teacher.help.learningStyle')} toggleLabel={t('dashboard.teacher.help.toggle')} />
                  {renderLearningStyle(summary?.learning_style)}
                  {renderDataSource(
                    summary?.learning_style?.confidence !== undefined
                      ? t('dashboard.teacher.sourcePaceStyleConf', { conf: typeof summary.learning_style.confidence === 'number' ? Math.round(summary.learning_style.confidence * 100) + '%' : summary.learning_style.confidence })
                      : t('dashboard.teacher.sourcePaceStyle')
                  )}
                </section>

                {/* Panel 4: 推定認知困難タイプ */}
                <section className="dashboard-panel card">
                  <h3 style={{ fontSize: '1.05em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                    {t('dashboard.teacher.estCognitiveDifficulty')}
                  </h3>
                  <PanelHelp text={t('dashboard.teacher.help.cognitive')} toggleLabel={t('dashboard.teacher.help.toggle')} />
                  {renderCognitiveDifficulty(summary?.cognitive_difficulty)}
                  {renderDataSource(t('dashboard.teacher.sourceQuestionnaireAi'))}
                </section>

                {/* Panel 5: よくある質問カテゴリ */}
                <section className="dashboard-panel card" style={{ gridColumn: '1 / -1' }}>
                  <h3 style={{ fontSize: '1.05em', color: 'var(--primary)', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
                    {t('dashboard.teacher.faqCategories')}
                  </h3>
                  <PanelHelp text={t('dashboard.teacher.help.faq')} toggleLabel={t('dashboard.teacher.help.toggle')} />
                  {(() => {
                    const cats = summary?.common_question_categories;
                    if (!cats || cats.length === 0) {
                      return renderEmptyChart(t('dashboard.teacher.noQuestionData'));
                    }
                    const maxCount = Math.max(...cats.map(c => c.count));
                    return (
                      <div style={{ padding: '8px 0' }}>
                        {cats.map((item, i) => (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            marginBottom: '10px', padding: '8px 12px', borderRadius: '8px',
                            background: selectedQuestionCategory === item.category ? 'rgba(90,138,122,0.12)' : 'transparent',
                            cursor: 'pointer', transition: 'background 0.2s',
                          }}
                          onClick={() => setSelectedQuestionCategory(
                            selectedQuestionCategory === item.category ? null : item.category
                          )}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(90,138,122,0.08)'}
                          onMouseLeave={(e) => e.currentTarget.style.background =
                            selectedQuestionCategory === item.category ? 'rgba(90,138,122,0.12)' : 'transparent'}
                          >
                            <span style={{
                              fontSize: '14px', fontWeight: 600, color: 'var(--text)',
                              minWidth: '140px',
                            }}>{item.category}</span>
                            <div style={{
                              flex: 1, height: '20px', borderRadius: '4px',
                              background: 'var(--bg-muted, #f0ebe3)',
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${(item.count / maxCount) * 100}%`,
                                height: '100%', borderRadius: '4px',
                                background: 'var(--primary, #5a8a7a)',
                                transition: 'width 0.8s ease',
                              }} />
                            </div>
                            <span style={{
                              fontSize: '14px', fontWeight: 700, color: 'var(--primary)',
                              minWidth: '40px', textAlign: 'right',
                            }}>{t('dashboard.teacher.timesCount', { count: item.count })}</span>
                          </div>
                        ))}
                        {selectedQuestionCategory && (
                          <div style={{
                            marginTop: '12px', padding: '12px', borderRadius: '8px',
                            background: 'rgba(90,138,122,0.06)', border: '1px solid var(--border)',
                            fontSize: '13px', color: 'var(--text-muted)',
                          }}>
                            {t('dashboard.teacher.categoryAdvice', { category: selectedQuestionCategory })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {renderDataSource(t('dashboard.teacher.sourceQuestionCategories'))}
                </section>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default TeacherDashboard;
